import { Injectable, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { AngularFirestore, CollectionReference, Query } from '@angular/fire/compat/firestore';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { FCM } from 'cordova-plugin-fcm-with-dependecy-updated/ionic/FCM';
import { LoggerService } from './logger.service';
import { StorageService } from './storage.service';
import { BadgeService } from './badge.service';

@Injectable({
  providedIn: 'root',
})
export class FcmService implements OnDestroy {
  userEmail: string;
  notificationSub: any;
  tokenRefreshSub?: Subscription;

  constructor(
    private platform: Platform,
    private afs: AngularFirestore,
    private logger: LoggerService,
    private storage: StorageService,
    private badgeService: BadgeService
  ) { }

  async checkNotifications(userEmail: string) {
    if (this.platform.is('cordova') && userEmail) {
      this.userEmail = userEmail;
      this.platform.ready().then(async () => {
        const hasPermission = await FCM.requestPushPermission();
        console.log({ hasPermission });
        if (hasPermission) {
          if (this.notificationSub) {
            this.notificationSub.unsubscribe();
          }
          const initialPushPayload: any = await FCM.getInitialPushPayload();
          if (initialPushPayload) {
            console.log(JSON.stringify(initialPushPayload));
          }
          if (this.platform.is('android')) {
            const default_notification_channel: any = {
              id: 'default_notification_channel',
              name: 'Push notification',
              description: 'General notifications',
              importance: 'default',
              visibility: 'public',
              lights: true,
              vibration: true,
            };
            await FCM.createNotificationChannel(default_notification_channel);
          }
          this.notificationSub = FCM.onNotification().subscribe((data: any) => {
            console.log('FCM NOTIFICATION:', JSON.stringify(data));
            const badge = Number(data?.badge || '0') || 0;
            console.log({ badge });
            if (data.wasTapped) {
              console.log('Received in background');
            } else {
              console.log('Received in foreground');
              this.badgeService.setBadge(badge);
            }
          });
          this.tokenRefreshSub = FCM.onTokenRefresh().subscribe(async fcm_token => {
            await this.saveFCMtoken(fcm_token);
          });
          const fcm_token = await FCM.getToken();
          console.log({ fcm_token });
          await this.saveFCMtoken(fcm_token);
        } else {
          console.log('NO_PERMISSION');
        }
      });
    }
  }

  async saveFCMtoken(token: string) {
    try {
      const tokens: string[] = [];
      const newToken = {
        id: null,
        created: null,
        lastUpdated: null,
        email: this.userEmail,
        tokens: tokens,
      };
      const existingToken = (await firstValueFrom(this.getFCMtokenByEmail(this.userEmail)))[0] || null;
      if (existingToken) {
        newToken.id = existingToken.id || null;
        newToken.tokens = existingToken.tokens || [];
        newToken.created = existingToken.created || null;
      }
      if (!newToken.tokens.includes(token)) {
        newToken.tokens.push(token);
      }
      await this.storage.set('fcm_token', token);
      await this.saveOrUpdateFCMToken(newToken);
      console.log('FCM TOKEN SAVED');
    } catch (error) {
      this.logger.handleError(error, true);
    }
  }

  async deleteFCMtoken(token: string) {
    try {
      const existingToken = (await firstValueFrom(this.getFCMtokenByEmail(this.userEmail)))[0] || null;
      if (existingToken) {
        existingToken.tokens = existingToken.tokens.filter((t: string) => t !== token);
        await this.saveOrUpdateFCMToken(existingToken);
      }
      await this.storage.set('fcm_token', null);
    } catch (error) {
      this.logger.handleError(error, true);
    }
  }

  saveOrUpdateFCMToken(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (data) {
        if (!data.id) {
          data.id = this.afs.createId();
        }
        if (!data.created) {
          data.created = new Date().toISOString();
        }
        data.lastUpdated = new Date().toISOString();
        this.afs
          .collection('FCM_tokens')
          .doc(data.id)
          .set(data)
          .then(() => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        reject();
      }
    });
  }

  getFCMtokenByEmail(email: string): Observable<any[]> {
    return this.afs
      .collection('FCM_tokens', ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('email', '==', email);
        return query;
      })
      .valueChanges() as Observable<any[]>;
  }

  ngOnDestroy() {
    this.notificationSub?.unsubscribe();
    this.tokenRefreshSub?.unsubscribe();
  }
}
